/**
 * Challenge standings for the admin Participants tab — a thin wrapper of the
 * `getChallengeStandings` callable (apps/functions/src/challengeAdmin.js).
 *
 * The tab needs the board WITHOUT the exclusions (hidden climbers too, so they
 * can be let back) and for a closed challenge too, so it cannot read the
 * precomputed `rankings/overall` / frozen `results/overall`. The server scores
 * the window with the one scorer every other board uses — for a route and a
 * spray challenge alike — so the client holds no copy of the scoring.
 */
import { doc, setDoc, deleteDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { callableRef } from "./callFunction";

const standingsCF = callableRef("getChallengeStandings");

/**
 * @returns {Promise<Array>} the unfiltered ranked rows of the challenge window
 *   (`{ rank, uid, excluded, totalPoints, …, displayName }`); `[]` without ids.
 */
export async function loadChallengeStandings(gymId, compId) {
  if (!gymId || !compId) return [];
  const res = await standingsCF({ gymId, compId });
  return Array.isArray(res?.data?.rankings) ? res.data.rankings : [];
}

/**
 * A climber's own challenge participation control doc
 * (`competitions/{compId}/participants/{uid}`), written from the competition
 * tab card and from the public board alike:
 *   auto  : { optedOut: true } to leave; { optedOut: false } to rejoin.
 *   opt_in: { joined: true } to join;    { joined: false } to leave.
 * When no flag stays truthy the doc is deleted (back to the mode's default).
 */
export async function writeChallengeParticipation({ gymId, compId, uid, patch = {} }) {
  if (!gymId || !compId || !uid) return;
  const ref = doc(db, "gyms", gymId, "competitions", compId, "participants", uid);
  const clean = {};
  if (patch.optedOut) clean.optedOut = true;
  if (patch.joined) clean.joined = true;
  if (Object.keys(clean).length === 0) {
    await deleteDoc(ref).catch(() => {});
    return;
  }
  await setDoc(ref, { ...clean, updatedAt: serverTimestamp() });
}
